import moment from 'moment'
import type { DrinkEntry } from '@/types'

// BAC calculation constants
export const METABOLISM_RATE = 0.015 // % BAC metabolized per hour (average liver processing rate)
export const ETHANOL_DENSITY = 0.789 // g/mL density of ethanol
export const GENDER_CONSTANT_MALE = 0.68 // Widmark factor for male (higher body water content)
export const GENDER_CONSTANT_FEMALE = 0.55 // Widmark factor for female (lower body water content)

/**
 * Coerce a possibly-malformed numeric field (string from KV/import/AI lookup)
 * to a finite number, falling back to 0. Without this, a non-numeric value
 * (e.g. "N/A") yields NaN, which poisons the running BAC accumulator and every
 * subsequent drink's BAC. Treating a corrupt drink as contributing 0 alcohol is
 * the safe failure mode.
 */
function safeNumber(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Calculate BAC for all entries in chronological order.
 * Uses the Widmark formula with metabolic decay.
 *
 * BAC = (alcohol_grams / (body_weight_grams × gender_constant)) × 100 - metabolic_decay
 */
export function calculateBAC(
  entries: DrinkEntry[],
  gender: 'male' | 'female',
  weight: number
): DrinkEntry[] {
  if (!entries || entries.length === 0) {
    return entries
  }

  const running = {
    alcohol_grams: 0,
    bac: 0,
  }

  // Gender constant (Widmark factor)
  const genderConstant = gender === 'male' ? GENDER_CONSTANT_MALE : GENDER_CONSTANT_FEMALE
  const weightConstant = weight * 1000 * genderConstant

  let sessionStart: string | false = false
  let alcoholGrams = 0
  let halfLife = 0

  const updatedEntries = [...entries]

  for (let i = 0; i < updatedEntries.length; i++) {
    const row = updatedEntries[i]

    // Skip gap entries
    if (row.is_gap) continue

    let diff = 0

    // Calculate time difference and BAC decay since last drink
    if (i > 0 && sessionStart) {
      diff = moment(row.finished_at).diff(moment(sessionStart), 'minutes')
      running.bac -= (diff / 60) * METABOLISM_RATE
      halfLife = (diff / 60) * METABOLISM_RATE
    }

    // Calculate alcohol grams in this drink
    // Formula: (alcohol_percentage / 100) × volume_ml × 0.789 (density of ethanol)
    alcoholGrams = (safeNumber(row.drink.alcohol) / 100) * safeNumber(row.drink.milliliters) * ETHANOL_DENSITY

    // Check if this starts a new session (BAC dropped to 0 since last drink)
    if (i < 1 || halfLife > (running.alcohol_grams / weightConstant) * 100) {
      halfLife = 0
      sessionStart = row.finished_at
      running.alcohol_grams = alcoholGrams
    } else {
      running.alcohol_grams += alcoholGrams
    }

    // Calculate current BAC
    running.bac = (running.alcohol_grams / weightConstant) * 100 - halfLife
    updatedEntries[i] = { ...row, bac: Math.max(0, running.bac) }
  }

  return updatedEntries
}

/**
 * Get current BAC based on last drink and time elapsed.
 */
export function getCurrentBAC(entries: DrinkEntry[]): number {
  if (!entries || entries.length === 0) return 0

  // Get the last non-gap entry
  const nonGapEntries = entries.filter(e => !e.is_gap)
  if (nonGapEntries.length === 0) return 0

  const lastEntry = nonGapEntries[nonGapEntries.length - 1]
  const lastBAC = lastEntry.bac || 0

  if (lastBAC <= 0) return 0

  // Calculate decay since last drink
  const hoursSinceLast = moment().diff(moment(lastEntry.finished_at), 'minutes') / 60
  const decay = hoursSinceLast * METABOLISM_RATE

  return Math.max(0, lastBAC - decay)
}

/**
 * Get hours until BAC reaches 0.
 */
export function getHoursUntilSober(currentBAC: number): number {
  if (currentBAC <= 0) return 0
  return currentBAC / METABOLISM_RATE
}

/**
 * Get seconds since last drink.
 */
export function getSecondsSinceLastDrink(entries: DrinkEntry[]): number {
  if (!entries || entries.length === 0) return 0

  const nonGapEntries = entries.filter(e => !e.is_gap)
  if (nonGapEntries.length === 0) return 0

  const lastEntry = nonGapEntries[nonGapEntries.length - 1]
  return moment().diff(moment(lastEntry.finished_at), 'seconds')
}

/**
 * Get BAC color class based on level.
 */
export function getBACColorClass(bac: number): string {
  if (bac >= 0.2) return 'bac-critical'
  if (bac >= 0.1) return 'bac-danger'
  if (bac >= 0.05) return 'bac-caution'
  if (bac >= 0.01) return 'bac-low'
  return 'bac-safe'
}

/**
 * Get BAC status text.
 */
export function getBACStatus(bac: number): string {
  if (bac >= 0.2) return 'Critical'
  if (bac >= 0.1) return 'Danger'
  if (bac >= 0.05) return 'Caution'
  if (bac >= 0.01) return 'Low'
  return 'Sober'
}

/**
 * Count drinking sessions based on BAC dropping to 0.
 */
export function countSessions(entries: DrinkEntry[]): number {
  if (!entries || entries.length === 0) return 0

  let sessionCount = 0
  const nonGapEntries = entries.filter(entry => !entry.is_gap)

  for (let i = 0; i < nonGapEntries.length; i++) {
    if (i === 0) {
      sessionCount = 1
      continue
    }

    const prevEntry = nonGapEntries[i - 1]
    const currentEntry = nonGapEntries[i]
    const timeToSober = (prevEntry.bac || 0) / METABOLISM_RATE

    const soberAt = moment(prevEntry.finished_at).add(timeToSober, 'hours')

    if (moment(currentEntry.finished_at).isAfter(soberAt)) {
      sessionCount++
    }
  }

  return sessionCount
}

/**
 * Count drinks in current session.
 * Returns 0 if BAC has fully decayed (user is sober).
 */
export function countCurrentSessionDrinks(entries: DrinkEntry[]): number {
  if (!entries || entries.length === 0) return 0

  const nonGapEntries = entries.filter(entry => !entry.is_gap)
  if (nonGapEntries.length === 0) return 0

  // If BAC has fully decayed since the last drink, session is over
  const lastEntry = nonGapEntries[nonGapEntries.length - 1]
  const lastBAC = lastEntry.bac || 0
  const hoursSinceLast = moment().diff(moment(lastEntry.finished_at), 'hours', true)
  if (lastBAC - hoursSinceLast * METABOLISM_RATE <= 0) {
    return 0
  }

  let count = 0

  for (let i = nonGapEntries.length - 1; i >= 0; i--) {
    count++

    if (i === 0) break

    const entry = nonGapEntries[i]
    const prevEntry = nonGapEntries[i - 1]
    const timeDiff = moment(entry.finished_at).diff(moment(prevEntry.finished_at), 'hours', true)
    const bacDecay = timeDiff * METABOLISM_RATE
    const prevBAC = prevEntry.bac || 0

    if (prevBAC - bacDecay <= 0) {
      break
    }
  }

  return count
}

/**
 * Assign a session index to every entry, parallel to the input array.
 *
 * Uses the same session-break rule as countSessions() and useDrinkLog's
 * `allSessions`: a new session starts when BAC would have fully decayed to
 * zero before the next drink was finished. Gap entries belong to no session
 * and get `null`.
 *
 * Returned indices are 0-based and contiguous in chronological order, so the
 * history table can band alternating sessions and the detail modal can report
 * "session N of M".
 */
export function computeSessionIndices(entries: DrinkEntry[]): (number | null)[] {
  const result: (number | null)[] = new Array(entries?.length || 0).fill(null)
  if (!entries || entries.length === 0) return result

  let sessionIndex = 0
  let prevEntry: DrinkEntry | null = null

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    if (entry.is_gap) continue

    if (prevEntry) {
      const timeToSober = (prevEntry.bac || 0) / METABOLISM_RATE
      const soberAt = moment(prevEntry.finished_at).add(timeToSober, 'hours')
      if (moment(entry.finished_at).isAfter(soberAt)) {
        sessionIndex++
      }
    }

    result[i] = sessionIndex
    prevEntry = entry
  }

  return result
}
